import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../hooks/useAuth';
import { useAvatar } from '../contexts/AvatarContext';
import { SubscriptionCard, SubscriptionUpgradeModal } from '../components/subscription';
import {
  ProfileHeader,
  ProfileStats,
  ProfileSidebar,
  ProfileForm,
  ProfileSection,
  ProfileLoading,
  ProfileError
} from '../components/profile';

function Profile() {
  const { user, loading: authLoading } = useAuth();
  const { avatar } = useAvatar();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [activeSection, setActiveSection] = useState('profile');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const addToast = useToast();

  // Mock stats data - in a real app, you'd fetch this from your API
  const [stats, setStats] = useState({
    totalProjects: 0,
    completedTasks: 0,
    activeProjects: 0,
    memberSince: null
  });

  const fetchProfile = useCallback(async () => {
    if (!user) return; // Don't fetch if user is not available
    
    try {
      setLoading(true);
      // Since we're using auth.users, we'll work with the user data directly
      // You might want to create a separate profiles table for additional user data
      setProfile({
        id: user.id,
        email: user.email,
        created_at: user.created_at,
        // Add any other fields you have in your user metadata
        first_name: user.user_metadata?.first_name || '',
        last_name: user.user_metadata?.last_name || '',
        username: user.user_metadata?.username || '',
      });
      
      setFirstName(user.user_metadata?.first_name || '');
      setLastName(user.user_metadata?.last_name || '');
      setUsername(user.user_metadata?.username || '');

      // Set basic stats
      setStats({
        totalProjects: 12, // Mock data - replace with real data
        completedTasks: 156,
        activeProjects: 4,
        memberSince: new Date(user.created_at).toLocaleDateString()
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      addToast('Error fetching profile: ' + error.message, 'danger');
    } finally {
      setLoading(false);
    }
  }, [user, addToast]);

  useEffect(() => {
    if (user) {
      fetchProfile();
    } else {
      // Clear profile data when user is not available
      setProfile(null);
      setLoading(false);
    }
  }, [user, fetchProfile]);

  const updateProfile = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          first_name: firstName,
          last_name: lastName,
          username: username,
        }
      });

      if (error) {
        throw error;
      }

      addToast('Profile updated successfully!', 'success');
      setEditing(false);
      fetchProfile(); // Refresh the profile data
    } catch (error) {
      console.error('Error updating profile:', error);
      addToast('Error updating profile: ' + error.message, 'danger');
    }
  };

  const getDisplayName = () => {
    if (profile?.first_name || profile?.last_name) {
      return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    }
    return profile?.username || 'User';
  };

  const getInitials = () => {
    if (avatar?.initials) {
      return avatar.initials;
    }
    
    const name = getDisplayName();
    if (name && name !== 'User') {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    
    // Default to first 3 chars of email
    return profile?.email?.substring(0, 3).toUpperCase() || 'U';
  };

  const renderProfileContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <div className="space-y-6">
            <ProfileHeader 
              profile={profile}
              stats={stats}
              editing={editing}
              setEditing={setEditing}
              getDisplayName={getDisplayName}
              getInitials={getInitials}
            />
            <ProfileStats stats={stats} />
            <ProfileForm 
              profile={profile}
              editing={editing}
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              username={username}
              setUsername={setUsername}
              updateProfile={updateProfile}
              setEditing={setEditing}
            />
          </div>
        );
      case 'subscription':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Subscription</h2>
                <p className="text-gray-600">Manage your subscription and billing</p>
              </div>
            </div>
            <SubscriptionCard onUpgrade={() => setShowUpgradeModal(true)} />
          </div>
        );
      case 'settings':
      case 'security':
      case 'activity':
        return <ProfileSection section={activeSection} />;
      default:
        return <ProfileSection section="profile" />;
    }
  };

  if (authLoading || loading) {
    return <ProfileLoading />;
  }

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-gray-500">Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return <ProfileError />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        <ProfileSidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
        />
        <div className="flex-1 min-w-0">
          {renderProfileContent()}
        </div>
      </div>
      
      <SubscriptionUpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </div>
  );
}

export default Profile;